import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  addCasaMapaAdminLeadNote,
  createCasaMapaAdminLead,
  fetchCasaMapaAdminLead,
  fetchCasaMapaAdminLeads,
  patchCasaMapaAdminLeadStatus,
} from "../../../api/clientCasamapaAdminApi";
import { useCasaMapaAdminPortal } from "../../../hooks/useCasaMapaAdminPortal";
import { useLocale } from "../../../intl";
import {
  buildLeadSearchHaystack,
  formatLeadDate,
  leadCategory,
  leadDisplayName,
  leadTypeI18nKey,
  parseLeadMetadata,
  partnerLeadStatusTone,
  PARTNER_LEAD_ACTIVE_STATUSES,
  PARTNER_LEAD_CLOSED_STATUSES,
  relativeLeadAge,
  summarizePartnerLeads,
  whatsappHref,
} from "../../../utils/casamapaPartnerLeadsPresentation";
import "../../../styles/partnerPortalDashboard.css";
import "../../../styles/partnerCasaMapaLeads.css";

const COPY = "realEstate.casaMapa.admin.partnerLeads";
const ADMIN = "realEstate.casaMapa.admin.leads";
const STATUS_FILTER_VALUES = ["", "new", "active", "closed"];
const CREATE_LEAD_TYPES = [
  "listing_inquiry",
  "schedule_showing",
  "request_info",
  "contact",
];

const EMPTY_CREATE = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  leadType: "listing_inquiry",
  listingTitle: "",
  message: "",
};

function PipelineBar({ label, value, max, tone = "blue" }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cm-dashboard__pipeline-row">
      <div className="cm-dashboard__pipeline-head">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="cm-dashboard__pipeline-track" aria-hidden="true">
        <div
          className={`cm-dashboard__pipeline-fill cm-dashboard__pipeline-fill--${tone}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint, tone = "slate" }) {
  return (
    <article className={`partner-kpi-card partner-kpi-card--${tone}`}>
      <span className="partner-kpi-card__label">{label}</span>
      <strong className="partner-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="partner-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

function LeadStatusBadge({ status, t }) {
  return (
    <span className={`partner-lead-badge partner-lead-badge--${partnerLeadStatusTone(status)}`}>
      {t(`${COPY}.status.${status}`, { defaultValue: status })}
    </span>
  );
}

function LeadCategoryBadge({ leadType, t }) {
  const category = leadCategory(leadType);
  return (
    <span className={`cm-partner-leads__type-badge cm-partner-leads__type-badge--${category}`}>
      {t(`${COPY}.categories.${category}`)}
    </span>
  );
}

function LeadListItem({ lead, selected, onSelect, t, locale }) {
  const meta = parseLeadMetadata(lead);
  const contextLine =
    lead.listing_title ||
    meta.listingTitle ||
    meta.listing_title ||
    meta.serviceCategory ||
    meta.vendorName ||
    t(`${COPY}.leadTypes.${leadTypeI18nKey(lead.lead_type)}`);
  const ageKey = relativeLeadAge(lead.lead_created_at, locale);
  const ageLabel =
    ageKey && ["just_now", "today", "yesterday", "this_week"].includes(ageKey)
      ? t(`${COPY}.age.${ageKey}`)
      : formatLeadDate(lead.lead_created_at, locale);

  return (
    <button
      type="button"
      className={`partner-lead-item cm-partner-leads__lead-card${
        selected ? " is-selected" : ""
      }${lead.partner_status === "new" ? " is-priority" : ""}`}
      onClick={() => onSelect(lead.id)}
    >
      <div className="partner-lead-item__top">
        <span className="partner-lead-item__number">{lead.lead_number}</span>
        <LeadStatusBadge status={lead.partner_status} t={t} />
      </div>
      <div className="partner-lead-item__top">
        <LeadCategoryBadge leadType={lead.lead_type} t={t} />
        {ageLabel ? <span className="partner-lead-item__date">{ageLabel}</span> : null}
      </div>
      <strong className="partner-lead-item__name">
        {leadDisplayName(lead) || t(`${ADMIN}.unnamedLead`)}
      </strong>
      <p className="cm-partner-leads__lead-context">{contextLine}</p>
      <p className="partner-lead-item__meta">
        {t(`${COPY}.leadTypes.${leadTypeI18nKey(lead.lead_type)}`)}
      </p>
      {lead.phone ? <span className="partner-lead-item__phone">{lead.phone}</span> : null}
    </button>
  );
}

function resolveNextStep(status, t) {
  if (status === "new") return t(`${COPY}.detail.nextSteps.new`);
  if (status === "accepted" || status === "contacted") return t(`${COPY}.detail.nextSteps.followUp`);
  if (status === "appointment_set" || status === "working") return t(`${COPY}.detail.nextSteps.working`);
  if (PARTNER_LEAD_CLOSED_STATUSES.includes(status)) return t(`${COPY}.detail.nextSteps.closed`);
  return t(`${COPY}.detail.nextSteps.default`);
}

function LeadDetailPanel({
  detail,
  listMeta,
  note,
  onNoteChange,
  onSubmitNote,
  onStatusChange,
  saving,
  t,
  locale,
}) {
  const metadata = listMeta || detail.metadata || {};
  const waHref = whatsappHref(detail.phone);
  const intent = metadata.casamapaIntent || metadata.intent;
  const timeline = metadata.timeline;
  const readiness = metadata.readiness;
  const preferredMethod = metadata.preferredContactMethod || metadata.preferredMethod;
  const listingTitle = detail.listingTitle || metadata.listingTitle || metadata.listing_title;
  const serviceLabel = metadata.serviceCategory || metadata.serviceName || metadata.vendorService;

  return (
    <div className="partner-lead-detail">
      <header className="partner-lead-detail__header">
        <div>
          <p className="partner-lead-detail__eyebrow">{detail.leadNumber}</p>
          <h2>{detail.contactName || t(`${ADMIN}.unnamedLead`)}</h2>
          <p className="partner-lead-detail__subtitle">
            {t(`${COPY}.leadTypes.${leadTypeI18nKey(detail.leadType)}`)}
          </p>
        </div>
        <LeadStatusBadge status={detail.partnerStatus} t={t} />
      </header>

      <div className="cm-partner-leads__detail-next">
        <p>{resolveNextStep(detail.partnerStatus, t)}</p>
        <div className="cm-partner-leads__detail-actions">
          {waHref ? (
            <a
              className="partner-dashboard-btn partner-dashboard-btn--primary partner-dashboard-btn--whatsapp"
              href={waHref}
              target="_blank"
              rel="noreferrer"
            >
              {t(`${COPY}.detail.whatsapp`)}
            </a>
          ) : null}
          {detail.partnerStatus === "new" ? (
            <button
              type="button"
              className="partner-dashboard-btn partner-dashboard-btn--primary"
              disabled={saving}
              onClick={() => onStatusChange("contacted")}
            >
              {t(`${COPY}.detail.markContacted`)}
            </button>
          ) : null}
          {["new", "contacted", "accepted"].includes(detail.partnerStatus) ? (
            <button
              type="button"
              className="partner-dashboard-btn"
              disabled={saving}
              onClick={() => onStatusChange("working")}
            >
              {t(`${COPY}.detail.markWorking`)}
            </button>
          ) : null}
          {!PARTNER_LEAD_CLOSED_STATUSES.includes(detail.partnerStatus) ? (
            <button
              type="button"
              className="partner-dashboard-btn"
              disabled={saving}
              onClick={() => onStatusChange("closed_won")}
            >
              {t(`${COPY}.detail.closeWon`)}
            </button>
          ) : null}
        </div>
      </div>

      <div className="partner-lead-detail__grid">
        <section className="partner-lead-detail__card">
          <h3>{t(`${COPY}.detail.contact`)}</h3>
          {detail.contactName ? <p>{detail.contactName}</p> : null}
          {detail.phone ? (
            <p>
              {t(`${COPY}.detail.phone`)}:{" "}
              {waHref ? (
                <a href={waHref} target="_blank" rel="noreferrer">
                  {detail.phone}
                </a>
              ) : (
                detail.phone
              )}
            </p>
          ) : null}
          {detail.email ? (
            <p>
              {t(`${COPY}.detail.email`)}:{" "}
              <a href={`mailto:${detail.email}`}>{detail.email}</a>
            </p>
          ) : null}
          {preferredMethod ? (
            <p className="muted">
              {t(`${COPY}.detail.preferredMethod`)}:{" "}
              {t(`realEstate.casaMapa.lead.preferred.${preferredMethod}`, {
                defaultValue: preferredMethod,
              })}
            </p>
          ) : null}
        </section>

        {listingTitle || serviceLabel ? (
          <section className="partner-lead-detail__card">
            <h3>
              {leadCategory(detail.leadType) === "property"
                ? t(`${COPY}.detail.propertyContext`)
                : t(`${COPY}.detail.serviceContext`)}
            </h3>
            {listingTitle ? <p>{listingTitle}</p> : null}
            {serviceLabel ? <p>{serviceLabel}</p> : null}
            {detail.pageUrl ? (
              <p>
                <a href={detail.pageUrl} target="_blank" rel="noreferrer">
                  {t(`${COPY}.detail.viewSourcePage`)}
                </a>
              </p>
            ) : null}
          </section>
        ) : null}

        {intent || timeline || readiness ? (
          <section className="partner-lead-detail__card partner-lead-detail__card--wide">
            <h3>{t(`${COPY}.detail.buyerPreferences`)}</h3>
            <div className="cm-partner-leads__meta-grid">
              {intent ? (
                <div className="cm-partner-leads__meta-item">
                  <span>{t(`${COPY}.detail.intent`)}</span>
                  <strong>
                    {t(`realEstate.casaMapa.lead.intents.${intent}`, {
                      defaultValue: intent,
                    })}
                  </strong>
                </div>
              ) : null}
              {timeline ? (
                <div className="cm-partner-leads__meta-item">
                  <span>{t(`${COPY}.detail.timeline`)}</span>
                  <strong>
                    {t(`realEstate.casaMapa.lead.timelines.${timeline}`, {
                      defaultValue: timeline,
                    })}
                  </strong>
                </div>
              ) : null}
              {readiness ? (
                <div className="cm-partner-leads__meta-item">
                  <span>{t(`${COPY}.detail.readiness`)}</span>
                  <strong>
                    {t(`realEstate.casaMapa.lead.readinessOptions.${readiness}`, {
                      defaultValue: readiness,
                    })}
                  </strong>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {detail.message ? (
          <section className="partner-lead-detail__card partner-lead-detail__card--wide">
            <h3>{t(`${COPY}.detail.message`)}</h3>
            <p>{detail.message}</p>
          </section>
        ) : null}

        {detail.createdAt ? (
          <section className="partner-lead-detail__card">
            <h3>{t(`${COPY}.detail.received`)}</h3>
            <p>{formatLeadDate(detail.createdAt, locale)}</p>
          </section>
        ) : null}
      </div>

      <section className="partner-lead-detail__notes">
        <h3>{t(`${COPY}.detail.notes`)}</h3>
        <label className="partner-lead-note-field">
          <span>{t(`${COPY}.detail.addNote`)}</span>
          <textarea rows={3} value={note} onChange={(event) => onNoteChange(event.target.value)} />
        </label>
        <button
          type="button"
          className="partner-dashboard-btn partner-dashboard-btn--primary"
          disabled={saving || !note.trim()}
          onClick={onSubmitNote}
        >
          {t(`${COPY}.detail.saveNote`)}
        </button>

        {detail.notes?.length ? (
          <ul className="partner-lead-notes-list">
            {detail.notes.map((entry) => (
              <li key={entry.id}>
                <p>{entry.noteText}</p>
                {entry.createdAt ? (
                  <time dateTime={entry.createdAt}>{formatLeadDate(entry.createdAt, locale)}</time>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">{t(`${COPY}.detail.noNotes`)}</p>
        )}
      </section>
    </div>
  );
}

function matchesStatusFilter(lead, statusFilter) {
  if (!statusFilter) return true;
  if (statusFilter === "new") return lead.partner_status === "new";
  if (statusFilter === "active") return PARTNER_LEAD_ACTIVE_STATUSES.includes(lead.partner_status);
  if (statusFilter === "closed") return PARTNER_LEAD_CLOSED_STATUSES.includes(lead.partner_status);
  return lead.partner_status === statusFilter;
}

export default function CasaMapaAdminLeadsPage() {
  const { clientViewId, viewBase } = useCasaMapaAdminPortal();
  const { t, locale } = useLocale();
  const listingsPath = viewBase ? `${viewBase}/listings` : ".";

  const [leads, setLeads] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createError, setCreateError] = useState("");

  const load = useCallback(async () => {
    if (!clientViewId) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const body = await fetchCasaMapaAdminLeads(clientViewId, { limit: 100 });
      setLeads(body.leads || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.errors.load`));
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => summarizePartnerLeads(leads), [leads]);

  const filteredLeads = useMemo(() => {
    const query = search.trim().toLowerCase();
    return leads.filter((lead) => {
      if (!matchesStatusFilter(lead, statusFilter)) return false;
      if (categoryFilter === "property" && leadCategory(lead.lead_type) !== "property") return false;
      if (categoryFilter === "service" && leadCategory(lead.lead_type) !== "service") return false;
      if (query && !buildLeadSearchHaystack(lead).includes(query)) return false;
      return true;
    });
  }, [leads, search, statusFilter, categoryFilter]);

  const selectedListLead = useMemo(
    () => leads.find((lead) => lead.id === selectedId) || null,
    [leads, selectedId]
  );

  const openLead = useCallback(
    async (id) => {
      if (!clientViewId || !id) return;
      setSelectedId(id);
      try {
        setDetailLoading(true);
        setErrorMessage("");
        const body = await fetchCasaMapaAdminLead(clientViewId, id);
        setDetail(body.lead);
      } catch (error) {
        setErrorMessage(error.message || t(`${COPY}.errors.detail`));
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [clientViewId, t]
  );

  useEffect(() => {
    if (selectedId || loading || !filteredLeads.length) return;
    const firstNew = filteredLeads.find((lead) => lead.partner_status === "new");
    openLead((firstNew || filteredLeads[0]).id);
  }, [filteredLeads, loading, openLead, selectedId]);

  const updateStatus = async (partnerStatus) => {
    if (!clientViewId || !selectedId) return;
    try {
      setSaving(true);
      await patchCasaMapaAdminLeadStatus(clientViewId, selectedId, partnerStatus);
      await openLead(selectedId);
      await load();
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.errors.update`));
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async () => {
    if (!clientViewId || !selectedId || !note.trim()) return;
    try {
      setSaving(true);
      await addCasaMapaAdminLeadNote(clientViewId, selectedId, note.trim());
      setNote("");
      await openLead(selectedId);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY}.errors.note`));
    } finally {
      setSaving(false);
    }
  };

  const submitCreate = async (event) => {
    event.preventDefault();
    if (!clientViewId) return;
    if (!createForm.firstName.trim()) {
      setCreateError(t(`${ADMIN}.create.nameRequired`));
      return;
    }
    if (!createForm.email.trim() && !createForm.phone.trim()) {
      setCreateError(t(`${ADMIN}.create.contactRequired`));
      return;
    }
    try {
      setSaving(true);
      setCreateError("");
      const body = await createCasaMapaAdminLead(clientViewId, {
        firstName: createForm.firstName.trim(),
        lastName: createForm.lastName.trim(),
        email: createForm.email.trim(),
        phone: createForm.phone.trim(),
        leadType: createForm.leadType,
        listingTitle: createForm.listingTitle.trim(),
        message: createForm.message.trim(),
      });
      setCreateForm(EMPTY_CREATE);
      setShowCreate(false);
      await load();
      if (body?.lead?.leadId) {
        await openLead(body.lead.leadId);
      }
    } catch (error) {
      setCreateError(error.message || t(`${COPY}.errors.update`));
    } finally {
      setSaving(false);
    }
  };

  const pipelineMax = Math.max(stats.pipeline.new, stats.pipeline.inProgress, stats.pipeline.closed, 1);

  return (
    <div className="cm-partner-leads partner-leads-page">
      <header className="partner-leads-hero cm-partner-leads__hero">
        <div>
          <p className="cm-partner-leads__eyebrow">{t(`${ADMIN}.eyebrow`)}</p>
          <h2>{t(`${ADMIN}.title`)}</h2>
          <p className="partner-dashboard-hero__intro">{t(`${ADMIN}.subtitle`)}</p>
        </div>
        <div className="cm-partner-leads__hero-actions">
          <button type="button" className="partner-dashboard-btn" onClick={load} disabled={loading}>
            {t(`${ADMIN}.refresh`)}
          </button>
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={() => setShowCreate((current) => !current)}
          >
            {t(`${ADMIN}.logInquiry`)}
          </button>
        </div>
      </header>

      {showCreate ? (
        <section className="partner-panel cm-partner-leads__create">
          <div>
            <h3>{t(`${ADMIN}.create.title`)}</h3>
            <p className="muted">{t(`${ADMIN}.create.body`)}</p>
          </div>
          <form className="cm-partner-leads__create-form" onSubmit={submitCreate}>
            <div className="cm-partner-leads__create-grid">
              <label>
                {t(`${ADMIN}.create.firstName`)}
                <input
                  value={createForm.firstName}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, firstName: event.target.value }))
                  }
                  autoComplete="given-name"
                />
              </label>
              <label>
                {t(`${ADMIN}.create.lastName`)}
                <input
                  value={createForm.lastName}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, lastName: event.target.value }))
                  }
                  autoComplete="family-name"
                />
              </label>
              <label>
                {t(`${ADMIN}.create.email`)}
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, email: event.target.value }))
                  }
                  autoComplete="email"
                />
              </label>
              <label>
                {t(`${ADMIN}.create.phone`)}
                <input
                  value={createForm.phone}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, phone: event.target.value }))
                  }
                  autoComplete="tel"
                />
              </label>
              <label>
                {t(`${ADMIN}.create.leadType`)}
                <select
                  value={createForm.leadType}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, leadType: event.target.value }))
                  }
                >
                  {CREATE_LEAD_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`${COPY}.leadTypes.${type}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t(`${ADMIN}.create.listingTitle`)}
                <input
                  value={createForm.listingTitle}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, listingTitle: event.target.value }))
                  }
                />
              </label>
              <label className="cm-partner-leads__create-wide">
                {t(`${ADMIN}.create.message`)}
                <textarea
                  rows={3}
                  value={createForm.message}
                  onChange={(event) =>
                    setCreateForm((current) => ({ ...current, message: event.target.value }))
                  }
                />
              </label>
            </div>
            {createError ? <p className="admin-data-health-error">{createError}</p> : null}
            <div className="cm-partner-leads__hero-actions">
              <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary" disabled={saving}>
                {saving ? t(`${ADMIN}.create.saving`) : t(`${ADMIN}.create.submit`)}
              </button>
              <button
                type="button"
                className="partner-dashboard-btn"
                onClick={() => {
                  setShowCreate(false);
                  setCreateError("");
                }}
              >
                {t(`${ADMIN}.create.cancel`)}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {stats.needsResponseCount > 0 ? (
        <section className="partner-panel cm-partner-leads__callout cm-partner-leads__callout--urgent">
          <div>
            <h2>{t(`${COPY}.priority.title`)}</h2>
            <p>{t(`${COPY}.priority.body`, { count: stats.needsResponseCount })}</p>
          </div>
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            onClick={() => {
              const next = leads.find((lead) => lead.partner_status === "new");
              if (next) openLead(next.id);
            }}
          >
            {t(`${COPY}.priority.action`)}
          </button>
        </section>
      ) : null}

      <section className="partner-kpi-grid" aria-label={t(`${ADMIN}.kpiLabel`)}>
        <KpiCard
          label={t(`${COPY}.kpi.new`)}
          value={stats.newCount}
          hint={t(`${COPY}.kpi.newHint`)}
          tone="blue"
        />
        <KpiCard
          label={t(`${COPY}.kpi.needsResponse`)}
          value={stats.needsResponseCount}
          hint={t(`${COPY}.kpi.needsResponseHint`)}
          tone="amber"
        />
        <KpiCard
          label={t(`${COPY}.kpi.inProgress`)}
          value={stats.inProgressCount}
          hint={t(`${COPY}.kpi.inProgressHint`)}
          tone="violet"
        />
        <KpiCard
          label={t(`${COPY}.kpi.closed`)}
          value={stats.closedCount}
          hint={t(`${COPY}.kpi.closedHint`)}
          tone="green"
        />
      </section>

      <div className="cm-partner-leads__overview">
        <section className="partner-panel cm-partner-leads__guidance">
          <h3>{t(`${COPY}.guidance.title`)}</h3>
          <ol>
            <li>
              <strong>{t(`${COPY}.guidance.step1Title`)}</strong>
              {t(`${COPY}.guidance.step1Body`)}
            </li>
            <li>
              <strong>{t(`${COPY}.guidance.step2Title`)}</strong>
              {t(`${COPY}.guidance.step2Body`)}
            </li>
            <li>
              <strong>{t(`${COPY}.guidance.step3Title`)}</strong>
              {t(`${COPY}.guidance.step3Body`)}
            </li>
          </ol>
          <p className="muted">
            {t(`${COPY}.guidance.agencyTip`)}{" "}
            <Link to={listingsPath}>{t(`${COPY}.guidance.listingsLink`)}</Link>
          </p>
        </section>

        <section className="partner-panel cm-partner-leads__pipeline">
          <h3>{t(`${COPY}.pipeline.title`)}</h3>
          <PipelineBar
            label={t(`${COPY}.pipeline.new`)}
            value={stats.pipeline.new}
            max={pipelineMax}
            tone="blue"
          />
          <PipelineBar
            label={t(`${COPY}.pipeline.inProgress`)}
            value={stats.pipeline.inProgress}
            max={pipelineMax}
            tone="amber"
          />
          <PipelineBar
            label={t(`${COPY}.pipeline.closed`)}
            value={stats.pipeline.closed}
            max={pipelineMax}
            tone="green"
          />
          {stats.propertyCount > 0 && stats.serviceCount > 0 ? (
            <p className="muted">
              {t(`${COPY}.pipeline.mixed`, {
                property: stats.propertyCount,
                service: stats.serviceCount,
              })}
            </p>
          ) : null}
        </section>
      </div>

      {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}

      <div className="cm-partner-leads__workspace">
        <aside className="partner-leads-sidebar partner-panel">
          <div className="cm-partner-leads__queue-header">
            <h3>{t(`${COPY}.queue.title`)}</h3>
            <p className="cm-partner-leads__queue-hint">{t(`${COPY}.queue.hint`)}</p>
          </div>

          <div className="partner-leads-sidebar__toolbar">
            <input
              type="search"
              className="partner-leads-search"
              placeholder={t(`${ADMIN}.searchPlaceholder`)}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="cm-partner-leads__filter-row" role="group" aria-label={t(`${COPY}.filters.status`)}>
            {STATUS_FILTER_VALUES.map((value) => {
              const labelKey = value || "all";
              return (
                <button
                  key={labelKey}
                  type="button"
                  className={`cm-partner-leads__chip${statusFilter === value ? " is-active" : ""}`}
                  onClick={() => setStatusFilter(value)}
                >
                  {t(`${COPY}.filters.${labelKey}`)}
                </button>
              );
            })}
          </div>

          {stats.propertyCount > 0 && stats.serviceCount > 0 ? (
            <div className="cm-partner-leads__filter-row" role="group" aria-label={t(`${COPY}.filters.category`)}>
              {["all", "property", "service"].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`cm-partner-leads__chip${categoryFilter === value ? " is-active" : ""}`}
                  onClick={() => setCategoryFilter(value)}
                >
                  {t(`${COPY}.filters.category_${value}`)}
                </button>
              ))}
            </div>
          ) : null}

          {loading ? <p className="muted partner-leads-sidebar__empty">{t(`${ADMIN}.loading`)}</p> : null}

          {!loading && filteredLeads.length === 0 ? (
            <div className="partner-leads-sidebar__empty cm-partner-leads__empty-state">
              <h3>{leads.length ? t(`${COPY}.emptyFilteredTitle`) : t(`${COPY}.emptyTitle`)}</h3>
              <p>{leads.length ? t(`${COPY}.emptyFilteredBody`) : t(`${COPY}.emptyBody`)}</p>
              {!leads.length ? (
                <button
                  type="button"
                  className="partner-dashboard-btn partner-dashboard-btn--primary"
                  onClick={() => setShowCreate(true)}
                >
                  {t(`${ADMIN}.logInquiry`)}
                </button>
              ) : null}
            </div>
          ) : null}

          {!loading && filteredLeads.length > 0 ? (
            <div className="partner-leads-list">
              {filteredLeads.map((lead) => (
                <LeadListItem
                  key={lead.id}
                  lead={lead}
                  selected={selectedId === lead.id}
                  onSelect={openLead}
                  t={t}
                  locale={locale}
                />
              ))}
            </div>
          ) : null}
        </aside>

        <section className="partner-leads-main partner-panel">
          {detailLoading ? <p className="muted">{t(`${COPY}.detail.loading`)}</p> : null}
          {!detailLoading && detail ? (
            <LeadDetailPanel
              detail={detail}
              listMeta={parseLeadMetadata(selectedListLead)}
              note={note}
              onNoteChange={setNote}
              onSubmitNote={submitNote}
              onStatusChange={updateStatus}
              saving={saving}
              t={t}
              locale={locale}
            />
          ) : null}
          {!detailLoading && !detail ? (
            <div className="partner-lead-detail partner-lead-detail--empty cm-partner-leads__empty-state">
              <span className="partner-lead-detail__empty-icon" aria-hidden="true">
                ✉
              </span>
              <h3>{t(`${COPY}.detail.emptyTitle`)}</h3>
              <p>{t(`${COPY}.detail.emptyBody`)}</p>
              <Link className="partner-dashboard-btn" to={listingsPath}>
                {t(`${ADMIN}.emptyListingsAction`)}
              </Link>
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
